import { useState } from "react"

import { Button } from "@/components/ui/button"
import {
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  ResponsiveDrawer,
} from "@/components/ui/drawer"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { StatusBadge } from "@/components/ui/status-badge"
import { CancelReasonDialog } from "@/features/staff/cancel-reason-dialog"
import { CANCEL_PATHS, orderCancelPayload, type CancellationReasonOption } from "@/features/staff/cancellations"
import { statusActionsFor, type OrderFlowAction } from "@/features/staff/order-flow"
import { intlLocale, t } from "@/lib/i18n"
import { patchInline, toastInlineUpdateError, toastInlineUpdateSuccess } from "@/lib/inline-update"
import { orderTone } from "@/lib/status-colors"

const I18N_BASE = "ui.react.features.staff.order_detail_drawer"

// ─── Types ─────────────────────────────────────────────────────────────────────

export type KanbanOrder = {
  id: number
  name: string
  status: string
  statusLabel: string
  serviceType: string
  serviceTypeLabel: string
  station: string
  stationLabel: string
  tableName: string | null
  billId: number
  customerName: string
  quantity: number
  notes: string
  price: string
  currency: string
  modifiers: {
    id: number
    name: string
    price: string
  }[]
  pickupEtaMinutes: number | null
  placedAt: string
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function formatPrice(price: string, currency: string) {
  const amount = Number(price)
  if (Number.isNaN(amount)) return `${price} ${currency.toUpperCase()}`

  return new Intl.NumberFormat(intlLocale(), {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount)
}

function formatPlacedAt(placedAt: string) {
  const parsed = Date.parse(placedAt)
  if (Number.isNaN(parsed)) return "—"

  return new Intl.DateTimeFormat(intlLocale(), { timeStyle: "short" }).format(new Date(parsed))
}

// ─── Component ─────────────────────────────────────────────────────────────────

export function OrderDetailDrawer({
  order,
  open,
  onOpenChange,
  cancellationReasons = [],
}: {
  order: KanbanOrder | null
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The closed cancellation catalog, straight from `Location::CancellationPolicy.options`. */
  cancellationReasons?: CancellationReasonOption[]
}) {
  const [submitting, setSubmitting] = useState(false)
  const [etaMinutes, setEtaMinutes] = useState("")

  if (!order) return null

  const actions = statusActionsFor(order)
  const requiresEta = actions.some((action) => action.requiresEta)
  const etaIsMissing = !etaMinutes || Number(etaMinutes) < 1

  const handleAction = (action: OrderFlowAction) => {
    const eta = action.requiresEta ? Number(etaMinutes) : undefined
    if (action.requiresEta && (!eta || eta < 1)) return

    const orderPayload = {
      status: action.status,
      ...(eta !== undefined ? { pickup_eta_minutes: eta } : {}),
    }

    setSubmitting(true)
    patchInline(`/orders/${order.id}`, { order: orderPayload }, { preserveState: true })
      .then((result) => {
        onOpenChange(false)
        setEtaMinutes("")
        toastInlineUpdateSuccess(result)
      })
      .catch((error: unknown) => toastInlineUpdateError(error))
      .finally(() => setSubmitting(false))
  }

  const handleCancelled = () => {
    onOpenChange(false)
    setEtaMinutes("")
  }

  return (
    <ResponsiveDrawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader className="pb-2">
          <DrawerTitle>
            <span className="font-normal text-muted-foreground tabular-nums">#{order.id}</span>
            {" · "}
            {order.name}
          </DrawerTitle>
          <DrawerDescription asChild>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <StatusBadge tone={orderTone(order.status)} label={order.statusLabel} />
              <span className="text-xs text-muted-foreground">{order.serviceTypeLabel}</span>
            </div>
          </DrawerDescription>
        </DrawerHeader>

        <div className="flex-1 space-y-5 overflow-y-auto px-4 pb-4">
          {/* ── Info grid ──────────────────────────────────────────────── */}
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <dt className="text-muted-foreground">{t(`${I18N_BASE}.table`)}</dt>
            <dd>{order.tableName ?? t("ui.common.no_table")}</dd>

            <dt className="text-muted-foreground">{t(`${I18N_BASE}.bill`)}</dt>
            <dd className="tabular-nums">#{order.billId}</dd>

            <dt className="text-muted-foreground">{t(`${I18N_BASE}.customer`)}</dt>
            <dd className="truncate">{order.customerName}</dd>

            <dt className="text-muted-foreground">{t(`${I18N_BASE}.station`)}</dt>
            <dd>{order.stationLabel}</dd>

            <dt className="text-muted-foreground">{t(`${I18N_BASE}.placed_at`)}</dt>
            <dd className="tabular-nums">{formatPlacedAt(order.placedAt)}</dd>

            <dt className="text-muted-foreground">{t(`${I18N_BASE}.quantity`)}</dt>
            <dd className="tabular-nums">{order.quantity}</dd>

            <dt className="text-muted-foreground">{t(`${I18N_BASE}.price`)}</dt>
            <dd className="font-medium tabular-nums">{formatPrice(order.price, order.currency)}</dd>

            {order.pickupEtaMinutes !== null && (
              <>
                <dt className="text-muted-foreground">{t(`${I18N_BASE}.eta_pickup`)}</dt>
                <dd className="font-medium tabular-nums">
                  {order.pickupEtaMinutes} {t(`${I18N_BASE}.min`)}
                </dd>
              </>
            )}
          </dl>

          {/* ── Modifiers ──────────────────────────────────────────────── */}
          {order.modifiers.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-sm font-medium">{t(`${I18N_BASE}.extras`)}</p>
              <ul className="space-y-1">
                {order.modifiers.map((modifier) => (
                  <li key={modifier.id} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{modifier.name}</span>
                    <span className="font-medium tabular-nums">
                      {formatPrice(modifier.price, order.currency)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* ── Notes ─────────────────────────────────────────────────── */}
          {order.notes && (
            <div className="space-y-1">
              <p className="text-sm font-medium">{t(`${I18N_BASE}.notes`)}</p>
              <p className="rounded-md border bg-muted/30 px-3 py-2 text-sm">{order.notes}</p>
            </div>
          )}

          {/* ── ETA input (for pick_up → ready_for_pickup) ────────────── */}
          {requiresEta && (
            <div className="space-y-1.5 rounded-md border bg-card p-3 shadow-xs">
              <Label htmlFor="eta-input">{t(`${I18N_BASE}.eta_pickup_minutes`)}</Label>
              <Input
                id="eta-input"
                type="number"
                min="1"
                max="120"
                inputMode="numeric"
                placeholder={t(`${I18N_BASE}.example_10`)}
                value={etaMinutes}
                onChange={(event) => setEtaMinutes(event.target.value)}
              />
            </div>
          )}
        </div>

        {/* ── Actions ─────────────────────────────────────────────────── */}
        {actions.length > 0 && (
          <DrawerFooter className="pt-2">
            {actions.map((action) =>
              // Cancelling carries a mandatory reason (`Location::CancellationPolicy`),
              // which none of the other status changes do, so it is the one
              // action here that opens a dialog instead of firing on click.
              action.status === "cancelled" ? (
                <CancelReasonDialog
                  key={action.status}
                  path={CANCEL_PATHS.order(order.id)}
                  payload={orderCancelPayload}
                  triggerTestId={`order-detail-cancel-${order.id}`}
                  reasons={cancellationReasons}
                  disabled={submitting}
                  triggerLabel={t(action.labelKey)}
                  triggerVariant={action.variant === "outline" ? "outline" : "destructive"}
                  triggerSize="default"
                  onSuccess={handleCancelled}
                />
              ) : (
                <Button
                  key={action.status}
                  type="button"
                  variant={action.variant}
                  disabled={submitting || (action.requiresEta === true && etaIsMissing)}
                  onClick={() => handleAction(action)}
                  data-testid={`order-action-${action.status}`}
                >
                  {t(action.labelKey)}
                </Button>
              )
            )}
          </DrawerFooter>
        )}
      </DrawerContent>
    </ResponsiveDrawer>
  )
}
